import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import {
  Scissors, Clock, Euro, User, ChevronRight, ChevronLeft, Check,
  Phone, Mail, CalendarDays, Loader2, MapPin, AlertCircle, Users
} from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = ['service', 'staff', 'datetime', 'info', 'confirm'];
const STEP_LABELS = ['Servizio', 'Staff', 'Data e Ora', 'I tuoi dati', 'Conferma'];

const PublicBookingPage = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [salonData, setSalonData] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  // Selections
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null); // null = "any"
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '', email: '', notes: '' });

  const primaryColor = salonData?.salon?.color || '#14B8A6';

  // Fetch salon data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/booking/${slug}`);
        setSalonData(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Salone non trovato' : 'Errore nel caricamento');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // Fetch availability when date changes
  const fetchSlots = useCallback(async () => {
    if (!selectedService || !selectedDate) return;
    setSlotsLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const staffParam = selectedStaff ? selectedStaff.id : 'any';
      const res = await axios.get(`${API}/booking/${slug}/availability?service_id=${selectedService.id}&staff_id=${staffParam}&date=${dateStr}`);
      setSlots(res.data.slots || []);
    } catch (err) {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [selectedService, selectedStaff, selectedDate, slug]);

  useEffect(() => {
    if (selectedDate && selectedService) {
      setSelectedSlot(null);
      fetchSlots();
    }
  }, [selectedDate, fetchSlots, selectedService]);

  // Available staff for selected service
  const availableStaff = useMemo(() => {
    if (!selectedService || !salonData) return [];
    return salonData.staff.filter(s => s.services.includes(selectedService.id));
  }, [selectedService, salonData]);

  // Group services by category
  const servicesByCategory = useMemo(() => {
    if (!salonData) return {};
    const map = {};
    salonData.services.forEach(s => {
      const cat = s.category || 'Altro';
      if (!map[cat]) map[cat] = [];
      map[cat].push(s);
    });
    return map;
  }, [salonData]);

  const handleConfirm = async () => {
    if (!selectedService || !selectedSlot || !customerInfo.name || !customerInfo.phone) return;
    setSubmitting(true);
    try {
      const staffId = selectedStaff ? selectedStaff.id : selectedSlot.available_staff[0].id;
      const res = await axios.post(`${API}/booking/${slug}/confirm`, {
        service_id: selectedService.id,
        staff_id: staffId,
        start_time: selectedSlot.start_time,
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        customer_email: customerInfo.email,
        notes: customerInfo.notes,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setConfirmed(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nella prenotazione. Riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  const deposit = salonData?.deposit;
  const depositAmount = (deposit?.enabled && selectedService)
    ? Math.min(deposit.type === 'percent' ? selectedService.price * deposit.value / 100 : deposit.value, selectedService.price)
    : 0;

  const canNext = () => {    switch (step) {
      case 0: return !!selectedService;
      case 1: return true; // staff is optional (any)
      case 2: return !!selectedSlot;
      case 3: return customerInfo.name.trim() && customerInfo.phone.trim();
      default: return false;
    }
  };

  // ─── RENDER ───

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h1 className="text-xl font-semibold mb-2">{error}</h1>
        <p className="text-gray-500">Verifica che il link sia corretto.</p>
      </div>
    </div>
  );

  if (confirmed) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(135deg, ${primaryColor}15, ${primaryColor}05)` }}>
      <Card className="w-full max-w-md shadow-lg" data-testid="booking-confirmed">
        <CardContent className="p-8 text-center space-y-4">
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
            <Check className="w-8 h-8" style={{ color: primaryColor }} />
          </div>
          <h2 className="text-2xl font-bold">Prenotazione Confermata!</h2>
          <div className="space-y-2 text-sm text-gray-600">
            <p><strong>{confirmed.service_name}</strong> con {confirmed.staff_name}</p>
            <p className="flex items-center justify-center gap-1">
              <CalendarDays className="w-4 h-4" />
              {format(parseISO(confirmed.start_time), "EEEE d MMMM yyyy 'alle' HH:mm", { locale: it })}
            </p>
          </div>
          <p className="text-xs text-gray-400 pt-4">Riceverai una conferma via email se fornita.</p>
        </CardContent>
      </Card>
    </div>
  );

  const salon = salonData.salon;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${primaryColor}08 0%, #f9fafb 40%)` }} data-testid="public-booking-page">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          {salon.logo_url && (
            <img src={`${API}${salon.logo_url}`} alt="" className="w-10 h-10 rounded-lg object-contain" />
          )}
          <div>
            <h1 className="font-bold text-lg">{salon.name}</h1>
            {salon.city && <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{salon.address ? `${salon.address}, ` : ''}{salon.city}</p>}
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-2xl mx-auto px-4 py-4">
        <div className="flex items-center gap-1 mb-6">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all ${
                i < step ? 'text-white' : i === step ? 'text-white' : 'bg-gray-200 text-gray-500'
              }`} style={i <= step ? { backgroundColor: primaryColor } : {}}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className="flex-1 h-0.5 mx-1" style={{ backgroundColor: i < step ? primaryColor : '#e5e7eb' }} />
              )}
            </div>
          ))}
        </div>
        <p className="text-sm font-medium text-gray-500 mb-4">{STEP_LABELS[step]}</p>

        {/* Step 0: Select Service */}
        {step === 0 && (
          <div className="space-y-4" data-testid="step-service">
            {Object.entries(servicesByCategory).map(([cat, services]) => (
              <div key={cat}>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{cat}</h3>
                <div className="space-y-2">
                  {services.map(service => (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => { setSelectedService(service); setSelectedStaff(null); setSelectedSlot(null); }}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all text-left ${
                        selectedService?.id === service.id ? 'ring-2 bg-white shadow-sm' : 'bg-white hover:border-gray-300'
                      }`}
                      style={selectedService?.id === service.id ? { borderColor: primaryColor, ringColor: primaryColor } : {}}
                      data-testid={`service-${service.id}`}
                    >
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                        <Scissors className="w-5 h-5" style={{ color: primaryColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{service.name}</p>
                        {service.description && <p className="text-xs text-gray-500 truncate">{service.description}</p>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-semibold text-sm">{service.price > 0 ? `€${service.price.toFixed(2)}` : 'Gratis'}</p>
                        <p className="text-xs text-gray-400 flex items-center gap-0.5"><Clock className="w-3 h-3" />{service.duration} min</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Step 1: Select Staff */}
        {step === 1 && (
          <div className="space-y-2" data-testid="step-staff">
            {/* Any available option */}
            <button
              type="button"
              onClick={() => setSelectedStaff(null)}
              className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all text-left ${
                selectedStaff === null ? 'ring-2 bg-white shadow-sm' : 'bg-white hover:border-gray-300'
              }`}
              style={selectedStaff === null ? { borderColor: primaryColor } : {}}
              data-testid="staff-any"
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100">
                <Users className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="font-medium text-sm">Qualsiasi disponibile</p>
                <p className="text-xs text-gray-500">Verrà assegnato lo staff disponibile</p>
              </div>
            </button>
            {availableStaff.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSelectedStaff(s)}
                className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all text-left ${
                  selectedStaff?.id === s.id ? 'ring-2 bg-white shadow-sm' : 'bg-white hover:border-gray-300'
                }`}
                style={selectedStaff?.id === s.id ? { borderColor: primaryColor } : {}}
                data-testid={`staff-${s.id}`}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: s.avatar_color || primaryColor }}>
                  {s.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <p className="font-medium text-sm">{s.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{s.role}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Select Date & Time */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-datetime">
            <div className="bg-white rounded-xl border p-3 flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => { setSelectedDate(d); setSelectedSlot(null); }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                locale={it}
              />
            </div>
            {selectedDate && (
              <div>
                <Label className="text-sm mb-2 block">Orari disponibili — {format(selectedDate, 'EEEE d MMMM', { locale: it })}</Label>
                {slotsLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                ) : slots.length > 0 ? (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {slots.map(slot => (
                      <button
                        key={slot.start_time}
                        type="button"
                        onClick={() => setSelectedSlot(slot)}
                        className={`py-2.5 px-2 rounded-lg text-sm font-medium border transition-all ${
                          selectedSlot?.start_time === slot.start_time ? 'text-white shadow-sm' : 'bg-white hover:border-gray-300'
                        }`}
                        style={selectedSlot?.start_time === slot.start_time ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}
                        data-testid={`slot-${slot.display.replace(':', '')}`}
                      >
                        {slot.display}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-6">Nessuno slot disponibile per questa data</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Customer Info */}
        {step === 3 && (
          <div className="space-y-4 bg-white rounded-xl border p-5" data-testid="step-info">
            <div className="space-y-2">
              <Label>Nome e Cognome *</Label>
              <Input value={customerInfo.name} onChange={e => setCustomerInfo(p => ({ ...p, name: e.target.value }))} placeholder="Mario Rossi" data-testid="customer-name" />
            </div>
            <div className="space-y-2">
              <Label>Telefono *</Label>
              <Input type="tel" value={customerInfo.phone} onChange={e => setCustomerInfo(p => ({ ...p, phone: e.target.value }))} placeholder="+39 333 1234567" data-testid="customer-phone" />
            </div>
            <div className="space-y-2">
              <Label>Email (opzionale)</Label>
              <Input type="email" value={customerInfo.email} onChange={e => setCustomerInfo(p => ({ ...p, email: e.target.value }))} placeholder="mario@email.com" data-testid="customer-email" />
            </div>
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Textarea value={customerInfo.notes} onChange={e => setCustomerInfo(p => ({ ...p, notes: e.target.value }))} placeholder="Richieste particolari..." rows={2} data-testid="customer-notes" />
            </div>
          </div>
        )}

        {/* Step 4: Confirm */}
        {step === 4 && (
          <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="step-confirm">
            <h3 className="font-semibold">Riepilogo Prenotazione</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                <Scissors className="w-5 h-5" style={{ color: primaryColor }} />
                <div>
                  <p className="font-medium text-sm">{selectedService.name}</p>
                  <p className="text-xs text-gray-500">{selectedService.duration} min — €{selectedService.price.toFixed(2)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                <User className="w-5 h-5" style={{ color: primaryColor }} />
                <p className="text-sm">{selectedStaff ? selectedStaff.name : `${selectedSlot?.available_staff?.[0]?.name || 'Staff disponibile'}`}</p>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                <CalendarDays className="w-5 h-5" style={{ color: primaryColor }} />
                <p className="text-sm">{selectedDate && format(selectedDate, "EEEE d MMMM yyyy", { locale: it })} alle {selectedSlot?.display}</p>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                <Phone className="w-5 h-5" style={{ color: primaryColor }} />
                <div>
                  <p className="text-sm font-medium">{customerInfo.name}</p>
                  <p className="text-xs text-gray-500">{customerInfo.phone}{customerInfo.email ? ` · ${customerInfo.email}` : ''}</p>
                </div>
              </div>
              {customerInfo.notes && (
                <p className="text-xs text-gray-500 italic px-3">Note: {customerInfo.notes}</p>
              )}
              {depositAmount > 0 && (
                <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: `${primaryColor}12` }} data-testid="deposit-notice">
                  <div className="flex justify-between font-medium">
                    <span>Acconto da versare ora</span>
                    <span>€{depositAmount.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Il resto, €{(selectedService.price - depositAmount).toFixed(2)}, si salda in salone.
                    Pagamento sicuro con carta; l'orario resta tuo per 30 minuti.
                  </p>
                  {deposit?.policy && (
                    <p className="text-xs text-gray-500 mt-1.5 pt-1.5 border-t" data-testid="deposit-policy">
                      {deposit.policy}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-6 pb-8">
          {step > 0 ? (
            <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="btn-back">
              <ChevronLeft className="w-4 h-4 mr-1" />Indietro
            </Button>
          ) : <div />}

          {step < 4 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canNext()}
              style={{ backgroundColor: canNext() ? primaryColor : undefined }}
              data-testid="btn-next"
            >
              Avanti<ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleConfirm}
              disabled={submitting}
              style={{ backgroundColor: primaryColor }}
              data-testid="btn-confirm"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {depositAmount > 0 ? `Paga €${depositAmount.toFixed(2)} e prenota` : 'Conferma Prenotazione'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicBookingPage;
